<template>
  <v-dialog
    v-model="isDialogOpen"
    @keydown.escape="closeDialog"
    transition="slide-left"
    content-class="drawer-like"
    v-bind="$attrs">
    <v-card class="dialog-box" data-testid="create-task-dialog">
      <v-card-title class="d-flex flex-row align-center">
        <h2 class="typography-heading-lg">
          {{ t('yard.drawers.createTaskDrawer.headers.createNewTask') }}
        </h2>
        <v-spacer></v-spacer>
        <v-btn variant="text" icon="mdi-close" @click="closeDialog" />
      </v-card-title>

      <v-card-text class="dialog-body">
        <v-tabs v-model="tab" color="primary" class="custom-tabs">
          <v-tab value="move">{{ t('yard.drawers.createTaskDrawer.tabs.move') }}</v-tab>
          <v-tab value="check">{{ t('yard.drawers.createTaskDrawer.tabs.check') }}</v-tab>
        </v-tabs>

        <v-tabs-window v-model="tab">
          <!-- we use the key here just to force the form to re-render when the tab is changed -->
          <v-tabs-window-item value="move">
            <p
              class="typography-body-sm my-4"
              v-html="
                $t(
                  'yard.drawers.createTaskDrawer.messages.moveTasksLetYardDriversRecordAMoveInRealTimeFromTheirAppOnceCompletedTheMovementIsInstantlyUpdatedInTheYardView'
                )
              "></p>

            <yard-tasks-create-move-task-form
              :key="tab"
              @change="handleChange"
              @validate="handleValidate" />
          </v-tabs-window-item>
          <v-tabs-window-item value="check">
            <p
              class="typography-body-sm my-4"
              v-html="
                $t(
                  'yard.drawers.createTaskDrawer.messages.checkTasksLetYardDriversUpdateYardViewDetailsDirectlyFromTheirAppLikeInventoryCountsAssetIDsAndSpotConditions'
                )
              "></p>

            <yard-tasks-create-check-task-form
              :key="tab"
              @change="handleChange"
              @validate="handleValidate" />
          </v-tabs-window-item>
        </v-tabs-window>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="dialog-actions">
        <v-btn
          variant="text"
          @click="closeDialog"
          data-testid="create-check-task-dialog-action-nevermind">
          {{ t('global.buttons.nevermind') }}
        </v-btn>
        <primary-button
          :disabled="isSubmitDisabled"
          @click="createTask"
          data-testid="create-check-task-dialog-action-save">
          {{ t('global.buttons.save') }}
        </primary-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent, ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDialogListener, useNotify } from '@/composables';

import yardTasksService from '@satellite/services/yard-tasks-service';

import YardTasksCreateMoveTaskForm from './YardTasksCreateMoveTaskForm.vue';
import YardTasksCreateCheckTaskForm from './YardTasksCreateCheckTaskForm.vue';
import { transformCreateDataToApiRequestData } from './EventTriggeredCreateTaskDrawer.helpers';

export default defineComponent({
  name: 'EventTriggeredCreateTaskDrawer',
  components: {
    YardTasksCreateMoveTaskForm,
    YardTasksCreateCheckTaskForm
  },
  setup() {
    const { t } = useI18n();
    const notify = useNotify();

    const {
      isDialogOpen,
      dialogProps,
      closeDialog,
      subscribeToDialogEvent,
      unsubscribeFromDialogEvent
    } = useDialogListener('open-create-yard-task-drawer');

    onMounted(() => {
      subscribeToDialogEvent();
    });

    onUnmounted(() => {
      unsubscribeFromDialogEvent();
    });

    const tab = ref('move');
    const isSubmitDisabled = ref(true);
    const currentTaskData = ref(null);

    watch(isDialogOpen, newIsOpen => {
      if (!newIsOpen) {
        tab.value = 'move';
        currentTaskData.value = null;
      } else {
        tab.value = dialogProps.value?.type ?? 'move';
      }
    });

    watch(tab, () => {
      isSubmitDisabled.value = true;
    });

    const handleChange = task => {
      currentTaskData.value = task;
    };

    const handleValidate = isValid => {
      isSubmitDisabled.value = !isValid;
    };

    const createTask = async () => {
      if (!dialogProps.value) {
        console.error('Failed to create check task: No dialog props found');
        return;
      }

      const payload = transformCreateDataToApiRequestData(
        currentTaskData.value,
        dialogProps.value?.warehouseId,
        dialogProps.value?.warehouseTimezone
      );

      if (!payload) {
        console.error('Failed to build task payload');
        return;
      }

      try {
        const task = await yardTasksService.createYardTask(payload);

        notify(t(`yard.messages.${currentTaskData.value.type}TaskCreated`), 'success');

        dialogProps.value.onTaskCreated?.(task);
        closeDialog();
      } catch (error) {
        notify(t('yard.messages.failedToCreateYardTask'), 'error');
      }
    };

    return {
      t,
      tab,
      isDialogOpen,
      isSubmitDisabled,
      closeDialog,
      createTask,
      handleChange,
      handleValidate
    };
  }
});
</script>

<style scoped lang="scss">
:deep(.drawer-like) {
  width: 620px;
  max-width: 100%;
  position: fixed;
  right: 0;
  margin: 0;
  height: 100vh;
  max-height: 100%;

  .v-card {
    border-start-end-radius: 0;
    border-end-end-radius: 0;
  }
}
</style>
